<script setup>
  const authStore = useAuthStore()
  const localePath = useLocalePath()
  const { isIndividuel, isCollectif, isFirme } = useAuthData()
  const { isPresident } = useIsPresident()

  // Compute menu items based on user quality
  const menuItems = computed(() => {
    const base = [
      {
        name: 'auth-private',
        icon: 'tachometer-alt',
        label: 'pages.auth.private.dashboard.title',
        show: true
      }
    ]

    if (isIndividuel.value) {
      return [
        ...base,
        {
          name: 'auth-private-profile',
          icon: 'user',
          label: 'pages.auth.private.profile.title',
          show: true
        },
        {
          name: 'auth-private-my-documents',
          icon: 'file-contract',
          label: 'pages.auth.private.myDocuments.title',
          show: true
        },
        {
          name: 'auth-private-events',
          icon: 'calendar',
          label: 'pages.auth.private.events.title',
          show: true
        },
        {
          name: 'auth-private-commissions',
          icon: 'users',
          label: 'pages.auth.private.commissions.title',
          show: true
        },
        {
          name: 'auth-private-committees',
          icon: 'user-friends',
          label: 'pages.auth.private.committees.title',
          show: true
        },
        {
          name: 'auth-private-national-committees',
          icon: 'globe',
          label: 'pages.auth.private.nationalCommittees.title',
          show: true
        },
        // {
        //   name: 'auth-private-mandates',
        //   icon: 'scroll',
        //   label: 'pages.auth.private.mandates.title',
        //   show: true
        // },
        {
          name: 'auth-private-mandates',
          icon: 'scroll',
          label: 'pages.auth.private.mandates.title',
          show: true
        },
        {
          name: 'auth-private-president-communication',
          icon: 'envelope',
          label: 'pages.auth.private.presidentCommunication.title',
          show: isPresident.value
        },
        {
          name: 'auth-private-transactions',
          icon: 'file',
          label: 'pages.auth.private.transactions.title',
          show: true
        }
      ]
    }

    if (isCollectif.value) {
      return [
        ...base,
        {
          name: 'auth-private-bar-profile',
          icon: 'building',
          label: 'pages.auth.private.barProfile.title',
          show: true
        },
        {
          name: 'auth-private-bar-members',
          icon: 'users',
          label: 'pages.auth.private.barMembers.title',
          show: true
        },
        {
          name: 'auth-private-president-communication',
          icon: 'envelope',
          label: 'pages.auth.private.presidentCommunication.title',
          show: isPresident.value
        },
        {
          name: 'auth-private-transactions',
          icon: 'file',
          label: 'pages.auth.private.transactions.title',
          show: true
        }
      ]
    }

    if (isFirme.value) {
      return [
        ...base,
        {
          name: 'auth-private-firm-profile',
          icon: 'briefcase',
          label: 'pages.auth.private.firmProfile.title',
          show: true
        },
        {
          name: 'auth-private-firm-members',
          icon: 'users',
          label: 'pages.auth.private.firmMembers.title',
          show: true
        },
        {
          name: 'auth-private-president-communication',
          icon: 'envelope',
          label: 'pages.auth.private.presidentCommunication.title',
          show: isPresident.value
        },
        {
          name: 'auth-private-transactions',
          icon: 'file',
          label: 'pages.auth.private.transactions.title',
          show: true
        }
      ]
    }

    return base
  })
</script>

<template>
  <div class="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-sky-50/30">
    <!-- Public Header -->
    <header class="sticky top-0 z-[1100] w-full bg-white shadow-lg">
      <LayoutMenuTop class="hidden md:flex" />
      <LayoutMenuMain />
      <LayoutBreadCrumbs />
    </header>

    <!-- Private Area Container -->
    <div class="flex-1">
      <div class="container grid grid-cols-1 md:grid-cols-6 lg:grid-cols-8 gap-8 my-8">
        <!-- Sidebar Navigation -->
        <ClientOnly>
          <aside class="hidden md:block col-span-2">
            <div class="sticky top-40 gap-4 flex flex-col">
              <div class="bg-white rounded-md border border-slate-200 shadow-sm">
                <div class="flex h-16 shrink-0 items-center bg-gradient-to-r from-sky-50 to-sky-100 px-4 rounded-t-md">
                  <h1 class="text-lg font-semibold text-sky-900">{{ $t('pages.auth.menu.privateArea') }}</h1>
                </div>

                <!-- Navigation Links -->
                <div class="px-4 py-4">
                  <div class="text-xs font-semibold leading-6 text-slate-400 uppercase tracking-wide mb-2">
                    {{ $t('pages.auth.menu.myAccountSection') }}
                  </div>
                  <ul role="list" class="-mx-2 space-y-1">
                    <li v-for="item in menuItems" :key="item.name">
                      <NuxtLink
                        v-if="item.show"
                        :to="localePath({ name: item.name })"
                        class="group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-medium text-slate-700 hover:text-sky-600 hover:bg-sky-50 transition-colors"
                        active-class="!bg-sky-100 !text-sky-700 border-l-4 border-sky-500"
                        exact-active-class="!bg-sky-100 !text-sky-700 border-l-4 border-sky-500"
                      >
                        <font-awesome :icon="['fas', item.icon]" class="h-5 w-5 shrink-0" />
                        {{ $t(item.label) }}
                      </NuxtLink>
                    </li>
                  </ul>
                </div>

                <!-- Sign Out Button -->
                <div class="px-4 py-4 border-t border-slate-200">
                  <button
                    class="cursor-pointer group -mx-2 flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-medium text-slate-700 hover:text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                    @click="(authStore.logout(), $router.push('/'))"
                  >
                    <font-awesome :icon="['fas', 'sign-out-alt']" class="h-5 w-5 shrink-0" />
                    {{ $t('pages.auth.menu.disconnect') }}
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </ClientOnly>
        <!-- Main Content -->
        <main class="col-span-1 md:col-span-4 lg:col-span-6 flex flex-col gap-8">
          <slot />
        </main>
      </div>
    </div>

    <!-- Footer -->
    <footer class="border-t border-slate-200 bg-white">
      <LayoutFooterMain />
    </footer>
  </div>
</template>
